import {FrontEndNotification} from 'common'
import {adminRole} from 'common/enums'
import {IAppContext} from 'components/Context'
import React from 'react'
import {FirebaseInstances} from './helpers'
import {adminsApi, notificationsApi} from 'lib/api'
import {
  clearBrowserPushSubscription,
  subscribeBrowserPush
} from 'lib/push/browserPush'

const isTransientNotificationStreamError = (error: unknown) => {
  const message =
    (error instanceof Error && error.message) ||
    (typeof error === 'string' ? error : '')

  return /network error|failed to fetch|load failed|abort/i.test(message)
}

export function createNotificationService(fb: FirebaseInstances) {
  const updateLastViewedNotification = async () => {
    const uid = fb.auth.currentUser?.uid
    if (!uid) return {}
    try {
      await notificationsApi.markViewed()
      return {success: true}
    } catch (error) {
      return {error, success: false}
    }
  }

  const markAllNotiicationsAsRead = async () => {
    const uid = fb.auth.currentUser?.uid
    if (!uid) return {}
    try {
      await notificationsApi.markAllAsRead()
      return {success: true}
    } catch (error) {
      return {error, success: false}
    }
  }

  const markNotificationAsRead = async (notificationId?: string) => {
    if (!notificationId) return {}
    try {
      await notificationsApi.markAsRead(notificationId)
      return {success: true}
    } catch (error) {
      console.error(error)
      return {error}
    }
  }

  const getNotifications = (
    updateState?: React.Dispatch<React.SetStateAction<IAppContext['appState']>>
  ) => {
    const uid = fb.auth?.currentUser?.uid
    if (!uid) return

    return notificationsApi.stream<{
      notifications?: FrontEndNotification[]
    }>(
      {limit: '10'},
      {
        onMessage: (result) => {
          if (fb.auth?.currentUser?.uid !== uid) return
          const notifications = (result as any)?.notifications || []
          const arr = Array.isArray(notifications) ? notifications : []
          updateState?.((prev: IAppContext['appState']) => ({
            ...prev,
            notifications: arr,
            hasMoreNotifications: arr.length === 10
          }))
        },
        onError: (error) => {
          if (isTransientNotificationStreamError(error)) {
            return
          }
          console.error('Notification Error:', error)
        }
      }
    )
  }

  const getNextNotifications = async ({
    lastVisibleNotification: _lastVisibleNotification
  }: {
    lastVisibleNotification?: unknown
  }) => {
    const uid = fb.auth?.currentUser?.uid
    if (!uid) return
    try {
      const offset = _lastVisibleNotification ? '10' : '0'
      const result = await notificationsApi.list<{
        notifications: FrontEndNotification[]
      }>({limit: '10', offset})
      const notifications = (result as any)?.notifications || []
      return {notifications: Array.isArray(notifications) ? notifications : []}
    } catch (error) {
      return {error}
    }
  }

  const clearPushSubscription = async ({role}: {role: string}) => {
    const uid = fb.auth?.currentUser?.uid || ''
    if (!role || !uid) return
    try {
      await clearBrowserPushSubscription({
        clearSubscription: (endpoint) =>
          adminsApi.clearPushSubscription(endpoint)
      })
      localStorage.removeItem('hideNotificationBanner')
    } catch (error) {
      return {error}
    }
  }

  const requestNotifactionsPermission = async ({role}: {role: adminRole}) => {
    const uid = fb.auth?.currentUser?.uid
    if (!role || !uid) return
    try {
      return await subscribeBrowserPush({
        principalId: uid,
        loadConfig: () =>
          notificationsApi.getPushConfig<{
            enabled?: boolean
            publicKey?: string | null
          }>(),
        saveSubscription: (subscription) =>
          adminsApi.setPushSubscription(subscription)
      })
    } catch (error) {
      return {error}
    }
  }

  return {
    updateLastViewedNotification,
    markAllNotiicationsAsRead,
    markNotificationAsRead,
    getNotifications,
    getNextNotifications,
    clearPushSubscription,
    requestNotifactionsPermission
  }
}
